import {
  IconArrowBackUp,
  IconCash,
  IconCheck,
  IconClock,
  IconClockCancel,
  IconCreditCard,
} from "@tabler/icons-react";
import { Badge, Text, Button } from "@mantine/core";

/** Function to Generate Payment Modal Title */
export const paymentModalTitle = (title: string) => {
  return (
    <div className="payment-modal-title">
      <IconCreditCard size={24} />
      <h2>{title}</h2>
    </div>
  );
};

/** Function to Generate Label + Description with Blue Asterisk */
export const paymentInputFieldLabels = (label: string, description: string) => (
  <div className="payment-modal-form-labels">
    <Text fw={600}>
      {label} <span style={{ color: "#0077CC" }}>*</span>
    </Text>
    {description && (
      <Text c="dimmed" size="sm">
        {description}
      </Text>
    )}
  </div>
);

/**  Helper function to extract initials from full name */
export const getInitials = (name: string) => {
  const words = name.trim().split(" ");
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export const statusBadge = (status: string) => {
  switch (status) {
    case "refund_pending":
      return (
        <Badge
          leftSection={<IconCash size={15} />}
          color="gray"
          size="md"
          radius="sm"
        >
          Refund Pending
        </Badge>
      );
    case "paid":
      return (
        <Badge
          leftSection={<IconCheck size={15} />}
          color="#D7F7DC"
          c="green"
          size="md"
          radius="sm"
        >
          {status}
        </Badge>
      );
    case "expired":
      return (
        <Badge
          leftSection={<IconClockCancel size={15} />}
          color="red"
          size="md"
          radius="sm"
        >
          {status}
        </Badge>
      );
    case "pending":
      return (
        <Badge
          leftSection={<IconClock size={15} />}
          color="#FCF8E8"
          size="md"
          radius="sm"
          c="black"
        >
          {status}
        </Badge>
      );
    case "refunded":
      return (
        <Badge
          leftSection={<IconArrowBackUp size={15} />}
          color="#FEE9E7"
          c="red"
          size="md"
          radius="sm"
        >
          {status}
        </Badge>
      );
    default:
      return (
        <Badge color="gray" size="md" radius="sm">
          {status}
        </Badge>
      );
  }
};

/**** Function to Generate Refund Button */
export const refundButton = (
  status: string,
  paymentRequestId: string,
  isLoading: boolean,
  onRefundClick: (paymentRequestId: string) => void
) => {
  if (status === "paid") {
    return (
      <Button
        variant="outline"
        color="gray"
        leftSection={<IconArrowBackUp size={15} />}
        radius="md"
        size="xs"
        // Mantine prop: sets button to loading state with a spinner
        loading={isLoading}
        onClick={() => onRefundClick(paymentRequestId)}
      >
        Refund
      </Button>
    );
  }
  return null;
};

/**** Function to Generate Make Payment Button */
export const makePaymentButton = (onClick: () => void, loading?: boolean) => {
  return (
    <Button
      color="#0077cc"
      radius="md"
      size="sm"
      onClick={onClick}
      loading={loading}
    >
      Make a Payment
    </Button>
  );
};

export const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, ""); // Remove non-numeric characters
  const parsedValue = parseInt(numericValue, 10) || 0; // Parse to integer, default to 0
  return (parsedValue / 100).toFixed(2); // Convert cents to decimal format
};
